/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { CONTEXT_PATH } from '@gh/config';
import { ResourceLoader } from '@gh/core-external';
import { switchMap$ } from '@gh/rx/operators';
import { compact } from 'lodash';
import { Observable } from 'rxjs';

export enum GanttExtension {
  AutoScheduling = 'auto_scheduling',
  SmartRendering = 'smart_rendering',
  Tooltip = 'tooltip',
  Marker = 'marker',
  ExportPdf = 'exportpdf',
}

export type GanttChartLoadOptions = {
  extensions: GanttExtension[];
  skin?: string;
  locale?: string;
};

const DHTMLX_BASE_URL = `${CONTEXT_PATH}dhtmlx`;
const DHTMLX_MAIN_URL_JS = `${DHTMLX_BASE_URL}/dhtmlxgantt.js`;
const DHTMLX_MAIN_URL_CSS = `${DHTMLX_BASE_URL}/dhtmlxgantt.css`;

function getLibraryUrl(relative: string): string {
  return `${DHTMLX_BASE_URL}/${relative}`;
}

function getLibraryExtensionUrl(extension: string): string {
  return getLibraryUrl(`ext/dhtmlxgantt_${extension}.js`);
}

function getLibrarySkinUrl(extension: string): string {
  return getLibraryUrl(`skins/dhtmlxgantt_${extension}.css`);
}

function getLibraryLocaleUrl(code?: string): string {
  return getLibraryUrl(`locale/locale${code ? `_${code}` : ''}.js`);
}

@Injectable()
export class GanttChartLoader {
  constructor(private loader: ResourceLoader) {
  }

  load(options: GanttChartLoadOptions): Observable<boolean> {
    return this.loader.require(DHTMLX_MAIN_URL_JS, DHTMLX_MAIN_URL_CSS).pipe(
      switchMap$(() => this.loader.require(...compact([
        ...options.extensions.map(getLibraryExtensionUrl),
        options.skin ? getLibrarySkinUrl(options.skin) : void 0,
        options.locale ? getLibraryLocaleUrl(options.locale) : void 0]))));
  }

  verify(): void {
    this.loader.verify(DHTMLX_MAIN_URL_JS);
  }
}
